import _ from 'lodash';
import PropTypes from 'prop-types';
import hoistNonReactStatics from 'hoist-non-react-statics';
import { withRouter } from 'react-router';

import withDataDependencies from '@nerdwallet/structured-content/with-data-dependencies';

import { ALPS_DATA_DEPENDENCY_TYPES } from '../constants';
import {
  getAlpsDataIds,
  getAlpsDataModelExecutionIds,
  getAlpsDataProducts,
  getAlpsDataResultSizeLimit,
} from './utils';
import { unpackDataDependencyIds } from '../helpers';
import { useSetGlobalModelExecutionIds } from '../tracking';

/**
 * HOC that wraps a component with sugar, spice, and everything ALPS.
 * @name withAlpsDataDependency
 *
 * @param {Component} DataDependentComponent - The component to wrap with ALPS data provider support.
 * @param {Function} dataDependencies - The component specified data dependencies (include location href for clientside fetch support).
 * @param {String} [componentDataPropAlias] - The prop alias under which we should expect alps data.
 * @return {Component} - The wrapped component.
 */
export default (
  DataDependentComponent,
  dataDependencies,
  componentDataPropAlias = 'data'
) => {
  /**
   * We expect the data provider for ALPS to provide the ids as well as product data mapping.
   * If we're not ALPS-dependent, then we should assume that the requested ids are those specified by the builder.
   */
  const AlpsDataWrapper = (props) => {
    const { dataDependency, [componentDataPropAlias]: data, cardLimit } = props;

    let products = {};
    let requestedIds = [];
    let resultSizeLimit;
    let modelExecutionIds;
    if (_.includes(ALPS_DATA_DEPENDENCY_TYPES, dataDependency.type)) {
      requestedIds = getAlpsDataIds(data);
      products = getAlpsDataProducts(data);
      resultSizeLimit = getAlpsDataResultSizeLimit(data);
      modelExecutionIds = getAlpsDataModelExecutionIds(data);
    } else {
      products = _.get(data, 'value', {});
      requestedIds = unpackDataDependencyIds(dataDependency);
      resultSizeLimit = cardLimit;
    }

    /**
     * Set model execution ids provided by ALPS.
     * @see {@link https://nerdwallet.atlassian.net/wiki/spaces/marketplace/pages/448203211/Automated+Landing+Page+Service+ALPS+Project+Spec#AutomatedLandingPageService(ALPS)ProjectSpec-Front-endClient}
     */
    useSetGlobalModelExecutionIds(modelExecutionIds);

    return (
      <DataDependentComponent
        {...props}
        requestedIds={requestedIds}
        products={products}
        resultSizeLimit={resultSizeLimit}
      />
    );
  };

  AlpsDataWrapper.propTypes = {
    data: PropTypes.shape({
      loading: PropTypes.bool,
      error: PropTypes.object,
      value: PropTypes.oneOfType([
        // ALPS
        PropTypes.shape({
          ids: PropTypes.arrayOf(PropTypes.string),
          products: PropTypes.object,
        }),
        // Standard
        PropTypes.object,
      ]),
    }),
    dataDependency: PropTypes.shape({
      data: PropTypes.shape({
        ids: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })),
      }),
      type: PropTypes.string,
      productPage: PropTypes.string,
    }),
  };

  AlpsDataWrapper.defaultProps = {
    data: {},
    dataDependency: {},
  };

  // Bake-in available route information and the dataDependency mapper.
  const WrappedComponent = _.flow(
    (Component) => withDataDependencies(Component, dataDependencies),
    withRouter
  )(AlpsDataWrapper);

  return hoistNonReactStatics(WrappedComponent, DataDependentComponent);
};
